import type { ButtonProps } from '@naf-simba/elements-buttons';
import { useDocumentData } from '@hooks/Documents/useDocumentData';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { isInternalLink } from '@lib/isInternalLink';
import { CountdownBlockType } from '@appTypes/countdownBlockType';
import useSelector from '@redux/typedHooks';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';

type CountdownCtaButtonProps = Extract<ButtonProps, { to: unknown }> | Extract<ButtonProps, { href: string }>;

type CountdownCta = {
  text: string;
  buttonProps: CountdownCtaButtonProps;
};

type UseCountdownCtaParams = Pick<CountdownBlockType, 'linkBlock' | 'ctaButton'>;

export const useCountdownCta = ({ linkBlock, ctaButton }: UseCountdownCtaParams): CountdownCta | null => {
  const legacyInternalTarget = useDocumentUrlWithNode(linkBlock?.internalReference);
  const countdownReferenceId = ctaButton?.ctaInternalLink?.reference?._ref || '';
  const { data: referencedDocument } = useDocumentData(countdownReferenceId);
  const sanityInternalTarget = useDocumentUrlWithNode(
    ctaButton?.ctaInternalLink?.referenceComponent || referencedDocument,
  );
  const baseUrl = useSelector((state) => state.application.baseUrl);
  const trackCTA = useTrackCTA();

  // Sanity CTA: internal link
  if (ctaButton?.linkType === 'internalLink' && ctaButton.buttonText && sanityInternalTarget) {
    const { relativeLink } = isInternalLink(baseUrl, sanityInternalTarget);
    const url = relativeLink || sanityInternalTarget;
    const onClick = () => trackCTA({ cta_position: 'content', text: ctaButton.buttonText, url });
    return {
      text: ctaButton.buttonText,
      buttonProps: { to: url, onClick, className: 'CtaButton' },
    };
  }

  // Sanity CTA: external link
  if (ctaButton?.linkType === 'externalLink' && ctaButton.buttonText && ctaButton.url) {
    const onClick = () => trackCTA({ cta_position: 'content', text: ctaButton.buttonText, url: ctaButton.url });
    return {
      text: ctaButton.buttonText,
      buttonProps: {
        href: ctaButton.url,
        target: ctaButton.blank ? '_blank' : undefined,
        onClick,
        className: 'CtaButton',
      },
    };
  }

  // Legacy fallback: linkBlock
  if (legacyInternalTarget) {
    return {
      text: 'Gå til testen',
      buttonProps: { to: legacyInternalTarget },
    };
  }

  if (linkBlock?.externalTarget?.href) {
    return {
      text: 'Gå til testen',
      buttonProps: {
        href: linkBlock.externalTarget.href,
        target: linkBlock.externalTarget.blank ? '_blank' : undefined,
      },
    };
  }

  return null;
};
